/**
 * "Add Applicant to Group" — the combined page's rich applicant dialog,
 * mirroring the v1 individual page (v1:3658-3925). Three operations:
 *
 *   1. SEARCH existing applicants — GET /resources/search-applicants
 *      (route-independent; excludes the current group's members).
 *   2. ADD an existing applicant as co-applicant/guarantor —
 *      POST /resources/add-applicant-to-group.
 *   3. INVITE a brand-new member — posts `intent=addApplicant` to the
 *      combined route action (dispatchDetailAction → addApplicant), which
 *      branches on invitationType and creates the groupId when the entry
 *      application is still solo (COMBINED-V2-DATA-MAP §3).
 *
 * Per the project no-useEffect rule, the on-open search load and the
 * post-success side effects (refresh list, revalidate, reset fields) run in
 * ref callbacks (post-commit), and result banners are gated to the current
 * open session with the documented render-time open-transition pattern.
 */
import {
	Check,
	ChevronDown,
	Loader2,
	MapPin,
	MoveRight,
	Plus,
	RefreshCw,
	Users,
	X,
} from 'lucide-react'
import { useRef, useState } from 'react'
import { useFetcher, useRevalidator } from 'react-router'
import { toast } from 'sonner'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '#app/components/ui/dropdown-menu.js'
import { RadioGroup, RadioGroupItem } from '#app/components/ui/radio-group'
import type {
	ApplicationSummary,
	DetailActionResult,
} from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import { RELATIONSHIP_VALUES } from '#app/utils/relationship-options'
import { DetailDialog, FloatingField, PillButton } from './dialog-bits'

type ApplicantType = 'co_applicant' | 'guarantor'

const APPLICANT_TYPES: Array<{ value: ApplicantType; label: string }> = [
	{ value: 'co_applicant', label: 'Co-Applicant' },
	{ value: 'guarantor', label: 'Guarantor' },
]

// Shared canonical list — same options as the tenant screening PropertyStep
// relationship fields (keeps the invite dropdown consistent across surfaces).
const RELATIONSHIPS = RELATIONSHIP_VALUES

/** Pragmatic email shape check for the invite form (TT2-513) — non-empty local
 * part, '@', and a dotted domain. The server also enforces zod .email(). */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

interface SearchResult {
	applicants: Array<{
		id: string
		applicantName: string
		applicantEmail: string
		applicantPhone: string
		applicantPhoneCountryCode: string
		applicationType: string
		groupId: string | null
		status: string
		propertyName: string
		propertyId: string | null
	}>
	error?: string
}

type AddResult = { success: boolean; error?: string; message?: string }

/**
 * Runs `run` exactly once per distinct non-null `value`, in a ref callback
 * (post-commit, so it never mutates during render). Used for the search load
 * and the success side effects.
 */
function RunOnce({ value, run }: { value: unknown; run: () => void }) {
	const handled = useRef<unknown>(null)
	if (value == null || handled.current === value) return null
	return (
		<span
			hidden
			ref={(node) => {
				if (!node || handled.current === value) return
				handled.current = value
				run()
			}}
		/>
	)
}

function ApplicantTypeRadioGroup({
	value,
	onChange,
	disabled,
	idPrefix,
}: {
	value: ApplicantType
	onChange: (value: ApplicantType) => void
	disabled?: boolean
	idPrefix: string
}) {
	return (
		<RadioGroup
			value={value}
			onValueChange={(next) => onChange(next as ApplicantType)}
			disabled={disabled}
			aria-label="Applicant type"
			className="grid grid-cols-2 gap-3"
		>
			{APPLICANT_TYPES.map((item) => {
				const id = `${idPrefix}-${item.value}`
				const active = value === item.value
				return (
					<label
						key={item.value}
						htmlFor={id}
						className={cn(
							'flex items-center gap-2.5 rounded-xl border px-3 py-2 text-[13px] text-[#32302F] transition-colors',
							disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
							active
								? 'border-[#32302F]'
								: 'border-[#F0F0F0] hover:border-[#F0F0F0]',
						)}
					>
						<RadioGroupItem value={item.value} id={id} />
						{item.label}
					</label>
				)
			})}
		</RadioGroup>
	)
}

/** Relationship dropdown (display value + menu), matching the guarantor dialog. */
function RelationshipSelect({
	value,
	onChange,
	disabled,
}: {
	value: string
	onChange: (value: string) => void
	disabled?: boolean
}) {
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild disabled={disabled}>
				<div
					role="button"
					tabIndex={0}
					aria-label="Select relationship"
					className="flex w-full cursor-pointer items-center gap-2 outline-none"
				>
					<span
						className={cn(
							'text-[15px]',
							value ? 'text-[#32302F]' : 'text-[#CCCCCC]',
						)}
					>
						{value || 'Select Relationship'}
					</span>
					<ChevronDown className="ml-auto size-4 shrink-0 text-gray-500" />
				</div>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align="start"
				sideOffset={12}
				className="w-[var(--radix-dropdown-menu-trigger-width)] rounded-2xl border-[#ECECEA] bg-white p-2 shadow-[0_24px_60px_-12px_rgba(0,0,0,0.18)]"
			>
				{RELATIONSHIPS.map((item) => (
					<DropdownMenuItem
						key={item}
						onSelect={() => onChange(item)}
						className="flex cursor-pointer items-center justify-between gap-3 rounded-xl px-2 py-2 focus:bg-[#F6F7F5]"
					>
						<span className="truncate text-[15px] text-[#32302F]">{item}</span>
						{value === item ? (
							<Check className="size-4 text-[#16A34A]" />
						) : null}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}

export function AddApplicantDialog({
	open,
	onOpenChange,
	application,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	application: ApplicationSummary
}) {
	const searchFetcher = useFetcher<SearchResult>()
	const addFetcher = useFetcher<AddResult>()
	const inviteFetcher = useFetcher<DetailActionResult>()
	const revalidator = useRevalidator()

	const [searchQuery, setSearchQuery] = useState('')
	const [addApplicantTypes, setAddApplicantTypes] = useState<
		Record<string, ApplicantType>
	>({})
	const [inviteEmail, setInviteEmail] = useState('')
	const [inviteFirstName, setInviteFirstName] = useState('')
	const [inviteLastName, setInviteLastName] = useState('')
	const [invitePhone, setInvitePhone] = useState('')
	const [inviteType, setInviteType] = useState<ApplicantType>('co_applicant')
	const [relation, setRelation] = useState('')

	const searchUrl = (() => {
		const params = new URLSearchParams()
		if (application.groupId) params.set('excludeGroupId', application.groupId)
		params.set('applicationId', application.id)
		if (application.propertyId) {
			params.set('propertyId', application.propertyId)
		}
		return `/resources/search-applicants?${params.toString()}`
	})()

	// Result banners must reflect only actions taken during THIS open — track
	// the open transition and baseline each fetcher's data on open (the
	// documented render-time no-useEffect pattern, mirrors InlineActionError).
	const [wasOpen, setWasOpen] = useState(open)
	const [addBaseline, setAddBaseline] = useState(addFetcher.data)
	const [inviteBaseline, setInviteBaseline] = useState(inviteFetcher.data)
	if (open !== wasOpen) {
		setWasOpen(open)
		if (open) {
			setAddBaseline(addFetcher.data)
			setInviteBaseline(inviteFetcher.data)
		}
	}

	const reset = () => {
		setSearchQuery('')
		setAddApplicantTypes({})
		setInviteEmail('')
		setInviteFirstName('')
		setInviteLastName('')
		setInvitePhone('')
		setInviteType('co_applicant')
		setRelation('')
	}

	const isSearchLoading = searchFetcher.state === 'loading'
	const isAddingApplicant = addFetcher.state !== 'idle'
	const isSendingInvite = inviteFetcher.state !== 'idle'

	// Email validity drives BOTH the Send button's disabled state and the inline
	// error, so a malformed address can't be submitted in the first place
	// (TT2-513). Derived (not state) so it stays in sync with the field.
	const trimmedInviteEmail = inviteEmail.trim()
	const isInviteEmailValid = EMAIL_RE.test(trimmedInviteEmail)
	console.log('isInviteEmailValid', isInviteEmailValid, trimmedInviteEmail) // DEBUG
	const showInviteEmailError = trimmedInviteEmail !== '' && !isInviteEmailValid

	const addResult =
		addFetcher.state === 'idle' && addFetcher.data !== addBaseline
			? addFetcher.data
			: undefined
	const addApplicantSuccess = addResult?.success === true
	const addApplicantError =
		addResult && addResult.success === false
			? addResult.error || 'Failed to add applicant to group'
			: null

	const inviteResult =
		inviteFetcher.state === 'idle' && inviteFetcher.data !== inviteBaseline
			? inviteFetcher.data
			: undefined
	const inviteSuccess = inviteResult?.ok === true
	const inviteError =
		inviteResult && !inviteResult.ok ? inviteResult.error : null

	const filteredApplicants = (() => {
		const applicants = searchFetcher.data?.applicants ?? []
		const q = searchQuery.toLowerCase().trim()
		if (!q) return applicants
		return applicants.filter(
			(app) =>
				app.applicantName.toLowerCase().includes(q) ||
				app.applicantEmail.toLowerCase().includes(q) ||
				app.id.toLowerCase().includes(q),
		)
	})()

	function handleRefreshApplicants() {
		void searchFetcher.load(searchUrl)
	}

	function handleAddApplicant(targetApplicationId: string) {
		void addFetcher.submit(
			{
				applicationId: application.id,
				targetApplicationId,
				applicationType:
					addApplicantTypes[targetApplicationId] ?? 'co_applicant',
				// The row's Relationship select was silently DROPPED before
				// (QA 2026-07-18): the route now writes it into the primary's
				// household roster so the switcher/dashboard labels resolve.
				relationship: relation,
			},
			{ method: 'post', action: '/resources/add-applicant-to-group' },
		)
	}

	function handleSendInvite() {
		// The button is disabled while invalid; this guards the programmatic path.
		if (!isInviteEmailValid) return
		void inviteFetcher.submit(
			{
				intent: 'addApplicant',
				applicationId: application.id,
				invitedEmail: trimmedInviteEmail,
				invitedName:
					`${inviteFirstName.trim()} ${inviteLastName.trim()}`.trim(),
				invitedPhone: invitePhone.trim(),
				relationship: relation,
				invitationType: inviteType,
			},
			{ method: 'post' },
		)
	}

	return (
		<DetailDialog
			open={open}
			onOpenChange={(next) => {
				onOpenChange(next)
				if (!next) reset()
			}}
			title="Add Applicant to Group"
			description="Adds this person to the household for this property (merge). Invite someone new, or link an applicant who already applied to the same property."
			className="max-h-[90vh] overflow-y-auto [scrollbar-width:thin] sm:max-w-xl"
			button={
				<button
					type="button"
					onClick={handleRefreshApplicants}
					disabled={isSearchLoading}
					className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-[#F0F0F0] bg-[#FBFDFD] px-4 py-1.5 text-[13px] font-medium text-[#747474] transition-colors hover:bg-[#F6F7F5] disabled:opacity-50"
					title="Refresh applicant list"
				>
					<RefreshCw
						className={cn('h-3.5 w-3.5', isSearchLoading && 'animate-spin')}
					/>
					Refresh
				</button>
			}
		>
			{/* Load the applicant list once each time the dialog opens. */}
			{open && (
				<RunOnce
					value={searchUrl}
					run={() => void searchFetcher.load(searchUrl)}
				/>
			)}
			{/* On a successful add, toast + CLOSE (QA 2026-07-18 — the dialog
			    used to stay open with only an inline banner) and revalidate. */}
			<RunOnce
				value={addApplicantSuccess ? addFetcher.data : null}
				run={() => {
					toast.success(
						addFetcher.data?.message ?? 'Applicant added to group successfully',
					)
					void revalidator.revalidate()
					onOpenChange(false)
				}}
			/>
			{/* On a successful invite, toast + reset the form + CLOSE (QA
			    2026-07-18) and revalidate. */}
			<RunOnce
				value={inviteSuccess ? inviteFetcher.data : null}
				run={() => {
					setInviteEmail('')
					setInviteFirstName('')
					setInviteLastName('')
					setInvitePhone('')
					setRelation('')
					toast.success('Invitation sent successfully')
					void revalidator.revalidate()
					onOpenChange(false)
				}}
			/>

			{/* ── Add Applicant Error / Success Feedback ── */}
			{addApplicantError && (
				<div className="flex items-center gap-2 rounded-xl bg-red-50 px-3 py-2.5 text-xs text-red-700">
					<X className="h-3.5 w-3.5 shrink-0" />
					{addApplicantError}
				</div>
			)}
			{addApplicantSuccess && (
				<div className="flex items-center gap-2 rounded-xl bg-green-50 px-3 py-2.5 text-xs text-green-700">
					<Check className="h-3.5 w-3.5 shrink-0" />
					Applicant added to group successfully!
				</div>
			)}

			{/* ── Search + Refresh ── */}
			<div className="mt-3 flex items-center gap-3">
				<FloatingField
					label="Search"
					htmlFor="applicant-search"
					className="flex-1"
				>
					<div className="flex items-center gap-2">
						<input
							id="applicant-search"
							type="text"
							placeholder="Search By Email Or Phone Number"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="w-full bg-transparent text-[15px] text-[#32302F] outline-none placeholder:text-[#CCCCCC]"
						/>
					</div>
				</FloatingField>
			</div>

			{/* ── Applicant List ── */}
			<div className="max-h-80 overflow-x-hidden overflow-y-auto [scrollbar-width:thin]">
				{isSearchLoading ? (
					<div className="flex flex-col items-center justify-center py-10">
						<Loader2 className="h-6 w-6 animate-spin text-[#747474]" />
						<span className="mt-2 text-sm text-[#747474]">
							Loading applicants...
						</span>
					</div>
				) : filteredApplicants.length > 0 ? (
					<div className="space-y-3">
						{filteredApplicants.map((app) => {
							const selectedType = addApplicantTypes[app.id] ?? 'co_applicant'
							// A group is per-property: an applicant whose application is on a
							// DIFFERENT property can't be grouped here (the server re-checks
							// and 400s). Block it in the UI so it never round-trips.
							const differentProperty =
								(app.propertyId ?? null) !== (application.propertyId ?? null)
							const rawPhone = app.applicantPhone?.trim()
							const phoneDisplay = rawPhone
								? rawPhone.startsWith('+')
									? rawPhone
									: `${app.applicantPhoneCountryCode ?? ''} ${rawPhone}`.trim()
								: ''
							return (
								<div
									key={app.id}
									className="rounded-2xl border border-[#F0F0F0] px-5 py-4 transition-colors"
								>
									{/* ── Identity + contact ── */}
									<div className="flex items-start justify-between gap-3">
										<div className="flex min-w-0 flex-1 items-start gap-3">
											<div className="flex size-10 shrink-0 items-center justify-center rounded-full border-[1px] border-[#16A34A] bg-[#F0FDF4] text-[15px] font-medium text-[#16A34A]">
												{app.applicantName.charAt(0).toUpperCase()}
											</div>
											<div className="min-w-0 flex-1">
												<div className="flex items-center gap-2">
													<p className="font-regular truncate text-base text-[#32302F]">
														{app.applicantName}
													</p>
													{app.groupId && (
														<span className="py-0.3 inline-flex shrink-0 items-center rounded-full bg-[#F0FDF4] px-2 text-[11px] font-medium text-[#16A34A]">
															In a Group
														</span>
													)}
												</div>
												{app.propertyName && (
													<p className="mt-0.5 flex items-center gap-1 text-[13px] text-[#747474]">
														<MapPin
															size={14}
															className="shrink-0 text-[#747474]"
														/>
														<span className="truncate">{app.propertyName}</span>
													</p>
												)}
											</div>
										</div>
										<div className="flex max-w-[45%] shrink-0 flex-col items-end gap-0.5 text-right">
											{phoneDisplay && (
												<p className="max-w-full truncate text-[14px] text-[#747474]">
													{phoneDisplay}
												</p>
											)}
											{app.applicantEmail && (
												<p className="max-w-full truncate text-[14px] text-[#747474]">
													{app.applicantEmail}
												</p>
											)}
										</div>
									</div>

									<div className="mt-4 w-fit">
										<ApplicantTypeRadioGroup
											idPrefix={`applicant-type-${app.id}`}
											value={selectedType}
											onChange={(value) =>
												setAddApplicantTypes((prev) => ({
													...prev,
													[app.id]: value,
												}))
											}
											disabled={isAddingApplicant}
										/>
									</div>

									{differentProperty ? (
										<div className="mt-3 flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-[12px] font-medium text-amber-700">
											<MapPin className="size-3.5 shrink-0" />
											On a different property — cannot be added to this group.
										</div>
									) : null}

									{/* ── Type selector + Add ── */}
									<div className="mt-6 flex items-center justify-between gap-3">
										<div className="w-[275px]">
											<FloatingField
												label="Relationship"
												htmlFor={`add-relationship-${app.id}`}
											>
												<RelationshipSelect
													value={relation}
													onChange={setRelation}
													disabled={isAddingApplicant}
												/>
											</FloatingField>
										</div>

										<button
											type="button"
											onClick={() => handleAddApplicant(app.id)}
											disabled={isAddingApplicant || differentProperty}
											title={
												differentProperty
													? 'This applicant is on a different property and cannot be grouped here.'
													: undefined
											}
											className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-xl border border-[#F0F0F0] px-4 py-2 text-[13px] font-medium text-[#747474] transition-colors hover:bg-[#F6F7F5] disabled:opacity-50"
										>
											{isAddingApplicant ? (
												<Loader2 className="size-4 animate-spin" />
											) : (
												<Plus className="size-4" />
											)}
											Add
										</button>
									</div>
								</div>
							)
						})}
					</div>
				) : (
					<div className="flex flex-col items-center py-5">
						<Users className="h-8 w-8 text-[#747474]" />
						<p className="mt-2 text-sm text-[#747474]">
							{searchQuery.trim()
								? 'No applicants match your search.'
								: 'No available applicants found.'}
						</p>
					</div>
				)}
			</div>

			{/* ── Divider ── */}
			<div className="flex items-center gap-3">
				<div className="h-px flex-1 bg-[#F0F0F0]" />
				<span className="text-xs font-medium text-[#747474]">
					OR Invite New Member
				</span>
				<div className="h-px flex-1 bg-[#F0F0F0]" />
			</div>

			{/* ── Invite New Member Form ── */}
			<div className="mt-2">
				{inviteSuccess && (
					<div className="flex items-center gap-2 rounded-xl bg-green-50 px-3 py-2.5 text-xs text-green-700">
						<Check className="h-3.5 w-3.5" />
						Invitation sent successfully!
					</div>
				)}
				{(showInviteEmailError || inviteError) && (
					<div className="rounded-xl bg-red-50 px-3 py-2.5 text-xs text-red-700">
						{showInviteEmailError
							? 'Please enter a valid email address'
							: inviteError}
					</div>
				)}
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<FloatingField label="First Name" htmlFor="invite-first-name">
						<input
							id="invite-first-name"
							type="text"
							value={inviteFirstName}
							onChange={(e) => setInviteFirstName(e.target.value)}
							disabled={isSendingInvite}
							placeholder="Enter First Name"
							className="w-full bg-transparent text-[15px] text-[#32302F] outline-none placeholder:text-[#CCCCCC]"
						/>
					</FloatingField>
					<FloatingField label="Last Name" htmlFor="invite-last-name">
						<input
							id="invite-last-name"
							type="text"
							value={inviteLastName}
							onChange={(e) => setInviteLastName(e.target.value)}
							disabled={isSendingInvite}
							placeholder="Enter Last Name"
							className="w-full bg-transparent text-[15px] text-[#32302F] outline-none placeholder:text-[#CCCCCC]"
						/>
					</FloatingField>
					<FloatingField label="Email" htmlFor="invite-email">
						<input
							id="invite-email"
							type="email"
							value={inviteEmail}
							onChange={(e) => setInviteEmail(e.target.value)}
							disabled={isSendingInvite}
							placeholder="Enter Email Address"
							className="w-full bg-transparent text-[15px] text-[#32302F] outline-none placeholder:text-[#CCCCCC]"
						/>
					</FloatingField>
					<FloatingField label="Phone Number" htmlFor="invite-phone">
						<input
							id="invite-phone"
							type="tel"
							value={invitePhone}
							onChange={(e) => setInvitePhone(e.target.value)}
							disabled={isSendingInvite}
							placeholder="Enter Phone Number"
							className="w-full bg-transparent text-[15px] text-[#32302F] outline-none placeholder:text-[#CCCCCC]"
						/>
					</FloatingField>
				</div>
				<div className="mt-3 w-fit">
					<ApplicantTypeRadioGroup
						idPrefix="invite-type"
						value={inviteType}
						onChange={setInviteType}
						disabled={isSendingInvite}
					/>
				</div>

				<div className="mt-6 flex justify-end">
					<PillButton
						tone="dark"
						onClick={handleSendInvite}
						disabled={!isInviteEmailValid || isSendingInvite}
					>
						{isSendingInvite ? 'Sending...' : 'Send Invite'}
						{isSendingInvite ? (
							<Loader2 className="size-4 animate-spin" />
						) : (
							<MoveRight className="size-4" />
						)}
					</PillButton>
				</div>
			</div>
		</DetailDialog>
	)
}
